import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Save, Eye, Code2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MagnetEditor — modal editor for a single guide. Lets admin edit title, subtitle,
 * and the full markdown body. Includes a preview tab.
 */
const MagnetEditor = ({ slug, onClose, onSaved, getAuthHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [tab, setTab] = useState('write'); // 'write' | 'preview'

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      const headers = getAuthHeaders();
      if (!headers) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/magnets/${slug}/full`, { headers });
        if (!cancelled) setDoc(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load guide');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, getAuthHeaders, onClose]);

  const set = (patch) => setDoc((prev) => ({ ...prev, ...patch }));

  const handleSave = async () => {
    if (!doc) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    setSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/magnets/${slug}`,
        {
          title: doc.title,
          subtitle: doc.subtitle,
          body_markdown: doc.body_markdown,
        },
        { headers, timeout: 30000 },
      );
      toast.success('Guide saved');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="magnet-editor-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">
        <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-medium">Edit guide</h2>
            <p className="text-xs text-gray-500 mt-0.5">{slug}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="magnet-editor-close">
            <X size={22} />
          </button>
        </header>

        {loading || !doc ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 border-b border-gray-200">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Title</label>
                <input
                  type="text"
                  value={doc.title || ''}
                  onChange={(e) => set({ title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="magnet-editor-title"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={doc.subtitle || ''}
                  onChange={(e) => set({ subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="magnet-editor-subtitle"
                />
              </div>
            </div>

            <div className="px-6 pt-4 flex items-center gap-2 border-b border-gray-200">
              <button
                onClick={() => setTab('write')}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px ${tab === 'write' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'}`}
                data-testid="magnet-editor-tab-write"
              >
                <Code2 size={14} /> Write (Markdown)
              </button>
              <button
                onClick={() => setTab('preview')}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px ${tab === 'preview' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-black'}`}
                data-testid="magnet-editor-tab-preview"
              >
                <Eye size={14} /> Preview
              </button>
              <span className="ml-auto text-xs text-gray-400 mb-2">{(doc.body_markdown || '').length.toLocaleString()} chars</span>
            </div>

            <div className="flex-1 overflow-auto px-6 py-4">
              {tab === 'write' ? (
                <textarea
                  value={doc.body_markdown || ''}
                  onChange={(e) => set({ body_markdown: e.target.value })}
                  className="w-full h-[55vh] px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm leading-6 resize-none"
                  data-testid="magnet-editor-body"
                  placeholder="## Section heading

Body text..."
                />
              ) : (
                <article
                  className="prose prose-lg max-w-none h-[55vh] overflow-auto px-2 prose-headings:font-light prose-h2:mt-8 prose-h2:mb-3 prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-base prose-pre:bg-gray-900 prose-pre:text-gray-50"
                  data-testid="magnet-editor-preview"
                >
                  <ReactMarkdown>{doc.body_markdown || '*(empty)*'}</ReactMarkdown>
                </article>
              )}
            </div>

            <footer className="px-6 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-black" data-testid="magnet-editor-cancel">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 text-sm"
                data-testid="magnet-editor-save"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                <span>{saving ? 'Saving…' : 'Save changes'}</span>
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default MagnetEditor;
